import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from 'recharts';
import type { PieSectorDataItem } from 'recharts/types/polar/Pie';
import type { GreenBeanProfile } from '../../../types/greenBean';
import { useLanguage } from '../../../features/language/useLanguage';
import { ForbiddenState } from '../../../components/common/ForbiddenState';

interface GreenBeanProfileInfographicsProps {
  profiles: GreenBeanProfile[];
  loading: boolean;
  isError: boolean;
  isForbidden?: boolean;
  showSpecies?: boolean;
  showVarietals?: boolean;
}

interface DistributionDatum {
  name: string;
  value: number;
  color: string;
}

const COLOR_PALETTE = ['#E7A8BC', '#9FD3EE', '#D9D9D9', '#C8B6FF', '#F7C59F', '#A8E6CF'];

const normalizeProfileValue = (value?: string) => {
  const normalized = value?.trim();
  return normalized && normalized.length > 0 ? normalized : 'Unknown';
};

const createDistributionData = (
  profiles: GreenBeanProfile[],
  key: 'species' | 'variety',
  maxSegments = 5
): DistributionDatum[] => {
  const grouped = profiles.reduce<Map<string, number>>((acc, profile) => {
    const label = normalizeProfileValue(profile[key]);
    if (label !== 'Unknown') {
      acc.set(label, (acc.get(label) ?? 0) + 1);
    }
    return acc;
  }, new Map());

  const sorted = Array.from(grouped.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  if (sorted.length <= maxSegments) {
    return sorted.map((item, index) => ({
      ...item,
      color: COLOR_PALETTE[index % COLOR_PALETTE.length],
    }));
  }

  const visible = sorted.slice(0, maxSegments);
  const othersValue = sorted.slice(maxSegments).reduce((sum, item) => sum + item.value, 0);
  const combined = [...visible, { name: 'Others', value: othersValue }];

  return combined.map((item, index) => ({
    ...item,
    color: COLOR_PALETTE[index % COLOR_PALETTE.length],
  }));
};

const renderActiveShape = (props: PieSectorDataItem) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;

  if (
    cx === undefined ||
    cy === undefined ||
    innerRadius === undefined ||
    outerRadius === undefined ||
    startAngle === undefined ||
    endAngle === undefined ||
    !fill
  ) {
    return null;
  }

  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      className="transition-all duration-300 ease-out"
    />
  );
};

type PieWithActiveProps = React.ComponentProps<typeof Pie> & {
  activeIndex?: number;
  activeShape?: (props: PieSectorDataItem) => React.ReactElement | null;
};

const PieWithActive = Pie as unknown as React.ComponentType<PieWithActiveProps>;

interface TooltipPayload {
  payload?: DistributionDatum;
}

interface DonutTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  total: number;
  profileLabel: (count: number) => string;
}

const DonutTooltip: React.FC<DonutTooltipProps> = ({ active, payload, total, profileLabel }) => {
  if (!active || !payload || payload.length === 0) return null;
  const datum = payload[0]?.payload;
  if (!datum) return null;
  const pct = total > 0 ? (datum.value / total) * 100 : 0;

  return (
    <div
      className="pointer-events-none rounded-xl border border-gray-200/80 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-sm"
      style={{ animation: 'tooltipFadeIn 0.15s ease-out forwards' }}
    >
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: datum.color }} />
        <span className="text-xs font-semibold text-gray-900">{datum.name}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-sm font-bold tabular-nums text-gray-900">{profileLabel(datum.value)}</span>
        <span className="text-[11px] font-medium text-gray-500">· {pct.toFixed(1)}%</span>
      </div>
    </div>
  );
};

interface HalfDonutProps {
  title: string;
  data: DistributionDatum[];
}

const HalfDonut: React.FC<HalfDonutProps> = ({ title, data }) => {
  const { t } = useLanguage();
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const profileLabel = (count: number) => t('dashboard.greenBeanInfographics.profileCount', { count });

  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="relative rounded-[24px] border border-gray-100 bg-white p-5 transition-all duration-300"
      data-test-id={`green-bean-${title.toLowerCase()}-chart-card`}
    >
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <span className="rounded-full bg-gray-50 px-2.5 py-0.5 text-[11px] font-medium text-gray-500">
          {data.length} {data.length === 1 ? 'category' : 'categories'}
        </span>
      </div>

      <div className="relative h-52 overflow-visible">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart style={{ overflow: 'visible' }}>
            <Tooltip
              cursor={false}
              wrapperStyle={{ outline: 'none', zIndex: 50 }}
              content={<DonutTooltip total={total} profileLabel={profileLabel} />}
            />
            {isVisible && (
              <PieWithActive
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(undefined)}
                data={data}
                dataKey="value"
                nameKey="name"
                startAngle={180}
                endAngle={0}
                cx="50%"
                cy="88%"
                innerRadius={55}
                outerRadius={120}
                paddingAngle={1}
                stroke="#FFFFFF"
                strokeWidth={2}
                animationDuration={1000}
              >
                {data.map((entry) => (
                  <Cell
                    key={`${title}-${entry.name}`}
                    fill={entry.color}
                    style={{ outline: 'none', cursor: 'pointer' }}
                  />
                ))}
              </PieWithActive>
            )}
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-x-0 bottom-6 z-0 flex flex-col items-center text-center">
          <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400">Total</p>
          <p className="text-2xl font-bold tabular-nums text-gray-900">{total}</p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">
        {data.map((item) => {
          const percentage = total > 0 ? (item.value / total) * 100 : 0;
          return (
            <div key={`${title}-legend-${item.name}`} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2 text-xs">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="truncate font-medium text-gray-700">{item.name}</span>
                </div>
                <span className="flex-shrink-0 text-xs font-semibold tabular-nums text-gray-900">
                  {item.value}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full transition-[width] duration-700 ease-out"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: item.color,
                    }}
                  />
                </div>
                <span className="w-10 flex-shrink-0 text-right text-[11px] font-medium tabular-nums text-gray-500">
                  {percentage.toFixed(1)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const GreenBeanProfileInfographics: React.FC<GreenBeanProfileInfographicsProps> = ({
  profiles,
  loading,
  isError,
  isForbidden = false,
  showSpecies = true,
  showVarietals = true,
}) => {
  const { t } = useLanguage();

  if (isForbidden) {
    return <ForbiddenState variant="card" />;
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="h-[340px] animate-pulse rounded-[24px] border border-gray-100 bg-white" />
        <div className="h-[340px] animate-pulse rounded-[24px] border border-gray-100 bg-white" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-4">
        <AlertTriangle className="h-5 w-5 text-red-600" />
        <p className="text-sm text-red-700">{t('dashboard.greenBeanInfographics.error')}</p>
      </div>
    );
  }

  if (profiles.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white p-6 text-center text-sm text-gray-500">
        {t('dashboard.greenBeanInfographics.empty')}
      </div>
    );
  }

  const speciesDistribution = createDistributionData(profiles, 'species');
  const varietyDistribution = createDistributionData(profiles, 'variety');
  const hasVisibleCharts = showSpecies || showVarietals;

  if (!hasVisibleCharts) {
    return null;
  }

  return (
    <div className="space-y-4">
      <style>{`
        @keyframes tooltipFadeIn {
          from { opacity: 0; transform: translateY(2px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div>
        <h2 className="text-lg font-bold text-gray-900">{t('dashboard.greenBeanInfographics.title')}</h2>
        <p className="text-sm text-gray-500">{t('dashboard.greenBeanInfographics.subtitle')}</p>
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {showSpecies && <HalfDonut title={t('dashboard.greenBeanInfographics.species')} data={speciesDistribution} />}
        {showVarietals && <HalfDonut title={t('dashboard.greenBeanInfographics.varietals')} data={varietyDistribution} />}
      </div>
    </div>
  );
};
